/**
 * Kory cell context — the structured description of a single table cell that
 * travels with a Kory message so the assistant can reason about an *exact*
 * value (not just the surrounding page).
 *
 * Deliberately table-agnostic: the scorecard is the first producer, but the
 * type + helpers are shared so other table surfaces (TanStack tables, WMS,
 * dashboards) attach the same shape later without inventing their own Kory
 * integration. The block carries the *displayed* facts (display value, raw
 * value, formatting rule, labels, period, filters, brand, freshness) so Kory
 * never has to guess the number; backend provenance (formula, source tables,
 * records) is resolved server-side by the `explain_cell` tool keyed off
 * `metricSlug` + `brand` + `periodId` + `weekStart`.
 */

/**
 * Producing surface for a captured cell. Known surfaces are listed for
 * autocomplete; the trailing `(string & {})` lets a new table adopt the shape
 * without editing this union (the value is only a label + dedupe discriminator).
 */
export type KoryCellSource = "scorecard" | "service-scorecard" | (string & {});

export interface KoryCellDrilldownTarget {
  brand: string;
  metric: string;
  period: string;
  weekStart: string;
}

export interface KoryCellContext {
  /** Stable id assigned at attach time — the dedupe/remove key for the chip list. */
  id: string;
  /** Discriminator for the producing surface; widen as other tables adopt this. */
  source: KoryCellSource;
  scorecardKind?: "weekly" | "sales" | "daily";

  // -- Page / table identity ----------------------------------------------
  pageTitle?: string;
  tableTitle?: string;
  /**
   * Stable page-owner key (nav-item id) for the route the cell was captured
   * on — resolved via `resolvePageOwner(pathname)` and stamped centrally at
   * attach time. Lets Kory route an investigation to-do to the page's
   * technical / business owner. Undefined on no-owner routes.
   */
  pageKey?: string;

  // -- Metric / column / row identity -------------------------------------
  /** Verbatim metric/row display name, e.g. "$ Service Revenue". */
  metricName: string;
  /** KPI registry slug, e.g. "service.revenue". Undefined ⇒ not a registry metric. */
  metricSlug?: string;
  metricLevel?: "KPI" | "PI";
  /** Column key, e.g. "actuals" | "target" | "num-var" | "pct-var" | "weekly-target". */
  columnId: string;
  /** Human column label, e.g. "Actuals" | "Target" | "# Variance". */
  columnLabel: string;
  /** Period key, e.g. "week" | "wtd" | "mtd" | "qtd" | "mon".."sun". */
  periodId: string;
  periodLabel?: string;
  /** Brand short_name — the row label AND the drill-through brand key ("Bell", "AT"). */
  brand: string;

  // -- Value (data accuracy) ----------------------------------------------
  /** Exactly what the user sees, e.g. "$205,855". */
  displayValue: string;
  /** The exact number behind the cell, e.g. 205855.23, or null for N/A. */
  rawValue: number | null;
  dataType?: "currency" | "number" | "percentage";
  /** Human description of the formatting/rounding rule applied to get displayValue. */
  formatRule: string;

  // -- Paired context (variance reasoning) --------------------------------
  pairedTarget?: number | null;
  pairedActuals?: number | null;
  /**
   * For an ambiguous column (variance / target), a single pre-composed line
   * stating EXACTLY what the user clicked — e.g. "# Variance: -$20,000 (the gap
   * vs target; negative = under)". Anchors Kory on the clicked figure so it
   * doesn't drift to the actuals that also sit in this block (pairedActuals).
   */
  clickedSummary?: string;

  // -- Scope / period / freshness -----------------------------------------
  /** ISO date (Monday) the scorecard week is anchored on. */
  weekStart?: string;
  /** Active filters in effect when the cell was selected (period mode, target version, …). */
  filters?: Record<string, string>;
  /** Source data freshness timestamp (ISO), when known. */
  dataAsOf?: string | null;
  isStale?: boolean;

  // -- Captured drilldown record ------------------------------------------
  /**
   * Set when this context is a single RECORD captured from a drilldown popup
   * (an invoice / deal / job behind an aggregate) rather than an aggregate
   * cell: the row's displayed fields, column label → string. Present ⇒
   * `supported` is false and Kory answers from these fields, not a formula.
   */
  record?: Record<string, string>;

  // -- Drilldown + support ------------------------------------------------
  /** The existing drilldown this cell opens, so Kory can offer "open the drilldown". */
  drilldownTarget?: KoryCellDrilldownTarget | null;
  /** True when the cell maps to a registry metric the backend can explain end-to-end. */
  supported: boolean;
}

/**
 * Two attached cells are "the same" when they point at the same brand+metric+
 * period+column — used to dedupe so re-clicking a cell doesn't stack chips.
 */
export function sameCell(a: KoryCellContext, b: KoryCellContext): boolean {
  // A captured drilldown record has no aggregate slug, so the aggregate key
  // below would collapse every record in the same drilldown into one. Dedupe
  // records by their serialized fields instead — re-clicking the same row is a
  // no-op, but two different rows stay distinct.
  if (a.record || b.record) {
    return (
      a.source === b.source &&
      a.brand === b.brand &&
      JSON.stringify(a.record) === JSON.stringify(b.record)
    );
  }
  return (
    a.source === b.source &&
    a.brand === b.brand &&
    a.metricSlug === b.metricSlug &&
    a.periodId === b.periodId &&
    a.columnId === b.columnId &&
    a.weekStart === b.weekStart
  );
}

/**
 * Serialize attached cells into the `<cell-context>` block prepended to the
 * outgoing Kory message — mirrors the proven `<page-context>` channel. The
 * body is a JSON array (structured + multi-cell) rather than XML attributes.
 * The backend strips this block from the displayed/history text (see
 * `_extract_cell_context`) while the model receives it verbatim.
 */
export function serializeCellContextBlock(cells: KoryCellContext[]): string {
  return [
    `<cell-context count="${cells.length}">`,
    JSON.stringify(cells),
    "</cell-context>",
  ].join("\n");
}

/**
 * Compact, human label for a context chip, e.g. "Revenue · Bell · $205,855".
 * The "Actuals" column is implied and omitted; other columns are annotated
 * (e.g. "Revenue (Target) · Bell · $210,000").
 */
export function cellChipLabel(cell: KoryCellContext): string {
  // A captured drilldown record: "<metric> record · <brand> · <salient fields>".
  if (cell.record) {
    return [`${cell.metricName} record`, cell.brand, cell.displayValue]
      .filter(Boolean)
      .join(" · ");
  }
  const metric =
    cell.columnLabel && cell.columnId !== "actuals"
      ? `${cell.metricName} (${cell.columnLabel})`
      : cell.metricName;
  return [metric, cell.brand, cell.displayValue].filter(Boolean).join(" · ");
}

/**
 * Human description of the formatting/rounding rule behind a displayValue,
 * derived from its dataType + column. Shared so every surface describes
 * formatting the same way.
 */
export function formatRuleFor(
  dataType: "currency" | "number" | "percentage" | undefined,
  columnId: string,
): string {
  if (columnId === "pct-var") return "Percent variance vs. target, signed (e.g. +22%)";
  switch (dataType) {
    case "currency":
      return "Currency (USD), rounded to the nearest dollar";
    case "percentage":
      return "Percentage";
    case "number":
      return "Number, rounded to a whole number";
    default:
      return "As displayed";
  }
}

/**
 * Build a KoryCellContext from a surface's *displayed* facts — the table-agnostic
 * entry point every non-scorecard table uses. Pass what the user sees (value,
 * label, brand, period); omit `metricSlug` ⇒ `supported:false`, so Kory answers
 * from the displayed facts and never fabricates backend provenance. `id` is
 * minted lazily here (build() runs only on attach).
 *
 * Pass `supported:false` alongside a `metricSlug` to carry the slug for LINKAGE
 * only — see the `supported` field below.
 */
export function makeCellContext(input: {
  source: KoryCellSource;
  metricName: string;
  brand: string;
  displayValue: string;
  rawValue: number | null;
  periodId: string;
  periodLabel?: string;
  columnId?: string;
  columnLabel?: string;
  dataType?: "currency" | "number" | "percentage";
  metricSlug?: string;
  /**
   * Explicit override for {@link KoryCellContext.supported}; defaults to
   * `Boolean(metricSlug)`. Set `false` to carry a slug for downstream LINKAGE
   * (the right-click menu's "Start KPI Investigation" / "Create issue" items
   * both key off `metricSlug`) while still telling Kory it can't explain_cell
   * this cell. Mirrors `KoryScorecardCellBase.supported` — slug ≠ supported.
   */
  supported?: boolean;
  /** ISO Monday — required by explain_cell once a metricSlug is set. */
  weekStart?: string;
  tableTitle?: string;
  pageTitle?: string;
  filters?: Record<string, string>;
  /** Pre-composed "what you clicked" line for ambiguous columns (variance/target). */
  clickedSummary?: string;
}): KoryCellContext {
  const columnId = input.columnId ?? "actuals";
  return {
    id: crypto.randomUUID(),
    source: input.source,
    metricName: input.metricName,
    metricSlug: input.metricSlug,
    weekStart: input.weekStart,
    columnId,
    columnLabel: input.columnLabel ?? "Actuals",
    periodId: input.periodId,
    periodLabel: input.periodLabel,
    brand: input.brand,
    displayValue: input.displayValue,
    rawValue: input.rawValue,
    dataType: input.dataType,
    formatRule: formatRuleFor(input.dataType, columnId),
    tableTitle: input.tableTitle,
    pageTitle: input.pageTitle,
    filters: input.filters,
    clickedSummary: input.clickedSummary,
    supported: input.supported ?? Boolean(input.metricSlug),
  };
}

/**
 * Build a KoryCellContext for a single RECORD clicked inside a drilldown popup
 * (an invoice / deal / job behind an aggregate) — the row's fields travel in
 * `record`, the parent aggregate's `metricName` / `brand` / period frame it.
 * Always `supported:false` (a record has no registry slug), so Kory answers
 * from the row's displayed fields and never fabricates a formula. `displayValue`
 * defaults to the first couple of populated fields when a call site doesn't pass
 * a salient label.
 */
export function makeRecordContext(input: {
  source: KoryCellSource;
  /** The aggregate this record sits behind, e.g. "Service Revenue". */
  metricName: string;
  brand: string;
  periodId: string;
  periodLabel?: string;
  tableTitle?: string;
  pageTitle?: string;
  /**
   * The parent aggregate's KPI slug + week, set when the drilldown was opened
   * from an explain_cell-backed cell. Present ⇒ supported:true, so Kory can call
   * explain_cell for the aggregate (definition, formula, exact SQL) and frame
   * this specific row against it. Omit for surfaces with no registry-backed
   * parent — the record stays descriptive.
   */
  metricSlug?: string;
  weekStart?: string;
  /** The row's displayed fields, column label → displayed string. */
  record: Record<string, string>;
  /** Compact chip label; defaults to the first one or two populated fields. */
  displayValue?: string;
  /** The row's primary numeric, when one column is the salient figure. */
  rawValue?: number | null;
  dataType?: "currency" | "number" | "percentage";
  filters?: Record<string, string>;
}): KoryCellContext {
  const populated = Object.values(input.record).filter((v) => v != null && v !== "");
  const displayValue = input.displayValue || populated.slice(0, 2).join(" · ") || "Record";
  return {
    id: crypto.randomUUID(),
    source: input.source,
    metricName: input.metricName,
    metricSlug: input.metricSlug,
    weekStart: input.weekStart,
    columnId: "record",
    columnLabel: "Record",
    periodId: input.periodId,
    periodLabel: input.periodLabel,
    brand: input.brand,
    displayValue,
    rawValue: input.rawValue ?? null,
    dataType: input.dataType,
    formatRule: "Individual record from the drilldown behind the aggregate",
    record: input.record,
    tableTitle: input.tableTitle,
    pageTitle: input.pageTitle,
    filters: input.filters,
    // A record with a parent slug is explain_cell-able for that aggregate; one
    // without stays descriptive (the row's fields only).
    supported: Boolean(input.metricSlug),
  };
}

/** The input accepted by {@link makeCellContext}, minus the `id` it mints. */
export type KoryCaptureInput = Parameters<typeof makeCellContext>[0];

/**
 * Props spread onto any DOM element to make it right-click-capturable via the
 * ambient handler in KoryProvider — no per-surface React wiring. The element
 * carries the cell's displayed facts as JSON; on right-click (or long-press),
 * the provider parses them, builds a context, and opens the "Ask Kory" menu.
 * Use for surfaces that aren't `<KoryCell>` table cells: stat tiles, chart
 * points, raw `<td>` tables.
 *
 *   <div {...koryCaptureProps({ source: "stat", metricName, brand, displayValue, rawValue, periodId })} />
 */
export function koryCaptureProps(input: KoryCaptureInput): {
  "data-kory-capture": string;
} {
  return { "data-kory-capture": JSON.stringify(input) };
}

/** What the cell menu's Explain sends about the captured value. */
export const EXPLAIN_CELL_PROMPT = "Explain this value";

/**
 * Quick replies surfaced once a cell is captured — each asks Kory about that
 * value. Shared by the tray, the header launcher and the /agent composer. The
 * menu's Explain already sends `EXPLAIN_CELL_PROMPT`, so it isn't one of them.
 */
export const CELL_PROMPTS = [
  "How is this calculated?",
  "What records make this up?",
  "Why did it change?",
];
